import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { Package, ClipboardCheck, Warehouse, ShoppingCart, TrendingUp, Truck, AlertTriangle, FileText, PackageCheck, Timer } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { getFlowDashboard, getMyFlowDashboard, getTatReport, getFirms } from '../../services/taskApi';

const FlowDashboard = () => {
  const { isDark, user } = useOutletContext();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tat, setTat] = useState(null);
  const [firms, setFirms] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [mine, setMine] = useState(false);

  const isSalesAgent = user?.flow_role === 'sales_agent' && user?.role !== 'admin';

  useEffect(() => {
    getFirms().then(r => setFirms(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const fetcher = mine || isSalesAgent ? getMyFlowDashboard : getFlowDashboard;
    fetcher(companyId || undefined).then(r => setStats(r.data)).catch(() => {});
    if (user?.role === 'admin' && !mine) {
      getTatReport({ days: 30, ...(companyId ? { company_id: companyId } : {}) }).then(r => setTat(r.data)).catch(() => setTat(null));
    } else {
      setTat(null);
    }
  }, [companyId, mine, isSalesAgent, user]);

  const cardCls = isDark ? 'bg-white/[0.04] border-white/10' : 'bg-white border-gray-200';
  const selectCls = isDark ? 'bg-white/5 border-white/10 text-white dark-select' : 'bg-white border-gray-300 text-gray-900';

  const statCards = stats ? [
    { label: 'Pending Approval', value: stats.sales.pending_approval, icon: ClipboardCheck, color: 'bg-amber-500', link: '/tasks/flow/approvals' },
    { label: 'Awaiting Tally Check', value: stats.items.awaiting_tally, icon: Warehouse, color: 'bg-blue-500', link: '/tasks/flow/store' },
    { label: 'Ready to Bill', value: stats.items.ready_to_pack, icon: FileText, color: 'bg-cyan-500', link: '/tasks/flow/billing' },
    { label: 'Billed - Pack & Push', value: stats.items.billed, icon: Package, color: 'bg-purple-500', link: '/tasks/flow/store' },
    { label: 'Awaiting Dispatch', value: stats.items.awaiting_dispatch, icon: Truck, color: 'bg-pink-500', link: '/tasks/flow/logistics' },
    { label: 'In Transit', value: stats.items.dispatched, icon: Truck, color: 'bg-indigo-500', link: '/tasks/flow/logistics' },
    { label: 'Delivered', value: stats.items.delivered, icon: PackageCheck, color: 'bg-green-500', link: '/tasks/flow/logistics' },
    { label: 'Awaiting PO', value: stats.procurement.awaiting_po, icon: ShoppingCart, color: 'bg-red-500', link: '/tasks/flow/purchase' },
    { label: 'Total Orders', value: stats.sales.total, icon: TrendingUp, color: 'bg-slate-600', link: '/tasks/flow/sales-orders' },
  ] : [];

  return (
    <div className="space-y-6" data-testid="flow-dashboard">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Flow Management</h1>
          <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Sales Order Pipeline & Inventory Flow</p>
        </div>
        <div className="flex items-center gap-3">
          {!isSalesAgent && (
            <label className={`flex items-center gap-2 text-xs cursor-pointer ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
              <input type="checkbox" checked={mine} onChange={e => setMine(e.target.checked)} data-testid="my-orders-toggle" />
              My orders only
            </label>
          )}
          <select
            className={`h-9 rounded-lg text-sm px-3 border min-w-[200px] ${selectCls}`}
            value={companyId} onChange={e => setCompanyId(e.target.value)}
            data-testid="flow-company-filter"
          >
            <option value="">All Companies</option>
            {firms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </div>
      </div>

      {!stats ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <div key={i} className={`h-28 rounded-xl animate-pulse ${isDark ? 'bg-white/5' : 'bg-gray-200'}`} />)}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {statCards.map((card, i) => (
              <Card
                key={i}
                className={`${cardCls} cursor-pointer hover:shadow-lg transition-all duration-200 group`}
                onClick={() => navigate(card.link)}
                data-testid={`flow-stat-${card.label.toLowerCase().replace(/[\s\/]/g, '-')}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{card.label}</p>
                      <p className={`text-3xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{card.value}</p>
                    </div>
                    <div className={`w-10 h-10 rounded-lg ${card.color} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                      <card.icon className="text-white" size={20} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex gap-3 flex-wrap">
            <Button onClick={() => navigate('/tasks/flow/sales-orders')} className="bg-blue-600 hover:bg-blue-700" data-testid="flow-goto-sales">
              <Package size={16} className="mr-2" /> Sales Orders
            </Button>
            {(user?.role === 'admin') && (
              <Button onClick={() => navigate('/tasks/flow/approvals')} variant="outline" className={isDark ? 'border-white/20 text-white hover:bg-white/10' : ''} data-testid="flow-goto-approvals">
                <ClipboardCheck size={16} className="mr-2" /> Approval Queue ({stats.sales.pending_approval})
              </Button>
            )}
            <Button onClick={() => navigate('/tasks/flow/store')} variant="outline" className={isDark ? 'border-white/20 text-white hover:bg-white/10' : ''} data-testid="flow-goto-store">
              <Warehouse size={16} className="mr-2" /> Store Dashboard
            </Button>
            <Button onClick={() => navigate('/tasks/flow/purchase')} variant="outline" className={isDark ? 'border-white/20 text-white hover:bg-white/10' : ''} data-testid="flow-goto-purchase">
              <ShoppingCart size={16} className="mr-2" /> Purchase Indents
            </Button>
          </div>

          {/* TAT Dashboard — Admin only */}
          {tat && (
            <Card className={cardCls} data-testid="tat-widget">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Timer size={18} className={isDark ? 'text-blue-400' : 'text-blue-600'} />
                    <p className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Turnaround Time (30-day avg, hours)</p>
                  </div>
                  <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{tat.completed_orders}/{tat.total_orders} completed</p>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-7 gap-3 text-xs">
                  {[
                    ['Sales→Approval', tat.averages.sales_to_approval_hrs],
                    ['Store/Tally', tat.averages.store_tally_hrs],
                    ['Billing', tat.averages.billing_hrs],
                    ['Packing', tat.averages.store_packing_hrs],
                    ['Dispatch', tat.averages.logistics_dispatch_hrs],
                    ['In Transit', tat.averages.delivery_transit_hrs],
                    ['Total', tat.averages.total_hrs],
                  ].map(([label, val]) => (
                    <div key={label} className={`text-center rounded-lg p-2 ${isDark ? 'bg-white/[0.02]' : 'bg-gray-50'}`}>
                      <p className={`text-[10px] uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{label}</p>
                      <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{val != null ? `${val}h` : '—'}</p>
                    </div>
                  ))}
                </div>
                {tat.pending_orders?.length > 0 && (
                  <details className="mt-3">
                    <summary className={`text-xs cursor-pointer ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{tat.pending_orders.length} in-progress orders (click to view age)</summary>
                    <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
                      {tat.pending_orders.slice(0, 20).map(p => (
                        <div key={p.so_number} className={`flex justify-between text-[11px] py-1 px-2 rounded ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}>
                          <span className={isDark ? 'text-blue-400' : 'text-blue-600'}>{p.so_number}</span>
                          <span className={isDark ? 'text-white/60' : 'text-gray-600'}>{p.customer}</span>
                          <span className={isDark ? 'text-white/40' : 'text-gray-500'}>{p.status}</span>
                          <span className={`font-medium ${p.age_hrs > 72 ? 'text-red-400' : isDark ? 'text-white/60' : 'text-gray-600'}`}>{p.age_hrs?.toFixed(1)}h</span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default FlowDashboard;
